import { useRef, useState } from 'react';

import { Button } from '@design-system';
import { AuthSeparator } from '@client/components';

import { PartnerConnectionForm } from './partner-connection-form';
import { PartnerShareConsentDialog } from './partner-share-consent-dialog';
import { PartnerConnectElectionDialog } from './partner-connect-election-dialog';
import { PartnerLegacyAttestationDialog } from './partner-legacy-attestation-dialog';
import { PartnerDisconnectDialog } from './partner-disconnect-dialog';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useIsDistributionPartnersForEdj } from './use-distribution-partners-for-edj';
import {
  useDistributionPartnerConnection,
  type DistributionContactWithPartner,
} from './use-distribution-partner-connection';

function contactDisplayName(contact: DistributionContactWithPartner): string {
  const name = `${contact.advisorFirstName ?? ''} ${contact.advisorLastName ?? ''}`.trim();

  return name || contact.advisorEmail;
}

interface PartnerConnectionSectionProps {
  variant: 'onboarding' | 'settings' | 'questionnaire';
  onConnected?: () => void;
}

export function PartnerConnectionSection({ variant, onConnected }: PartnerConnectionSectionProps) {
  // EDJ advisors get the reframed election experience: connecting is a factual "who I work with"
  // indication rather than a sharing directive, and existing clients are never auto-shared.
  const isEdjPilot = useIsEdjPilotAdvisor();
  const isEdjMode = useIsDistributionPartnersForEdj();
  // Legacy (pre-cutoff) client sharing never applies in either EDJ configuration.
  const hideLegacy = isEdjPilot || isEdjMode;
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [legacyDialogOpen, setLegacyDialogOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<DistributionContactWithPartner | null>(
    null,
  );
  // When the legacy attestation dialog is opened to share earlier clients with a specific set of
  // contacts (one from a contact's menu, or the batch just connected), this holds them; an empty
  // array means the global "share with everyone" flow.
  const [legacyContactTargets, setLegacyContactTargets] = useState<
    DistributionContactWithPartner[]
  >([]);

  const [futureShareTarget, setFutureShareTarget] = useState<DistributionContactWithPartner | null>(
    null,
  );
  // Read inside the connect-success callback without re-creating it on every change.
  const hasLegacyClientsRef = useRef(false);

  const connection = useDistributionPartnerConnection({
    onConnected: newlyConnected => {
      setConsentDialogOpen(false);
      // After connecting, if the advisor has pre-new-consent clients, offer the legacy attestation —
      // scoped to just the contacts they connected, so confirming shares earlier clients with only
      // those contacts rather than everyone they're already connected to.
      if (hasLegacyClientsRef.current && newlyConnected.length > 0) {
        setLegacyContactTargets(newlyConnected);
        setLegacyDialogOpen(true);
      }
      onConnected?.();
    },
  });

  // Offer the past-client consent right after an initial connection whenever the advisor has past
  // (pre-cutoff) clients — even if they've attested before, so they can extend that consent to the
  // newly connected contacts. This only runs on connect; the per-contact toggles handle it elsewhere.
  hasLegacyClientsRef.current = !hideLegacy && connection.hasLegacyClients;

  const dialogs = (
    <>
      {isEdjPilot ? (
        <PartnerConnectElectionDialog
          open={consentDialogOpen}
          isConnecting={connection.isConnecting}
          // 'all' → auto-share FUTURE clients only (shareExisting=false); 'specific' → nothing
          // shared automatically. Existing clients are always shared per-case via Share with
          // Supervisor.
          onConfirm={scope => connection.connect(scope === 'all', false)}
          onCancel={() => setConsentDialogOpen(false)}
        />
      ) : (
        <PartnerShareConsentDialog
          open={consentDialogOpen}
          defaultChecked={connection.shareConsent}
          isConnecting={connection.isConnecting}
          onConfirm={autoShare => connection.connect(autoShare)}
          onCancel={() => setConsentDialogOpen(false)}
        />
      )}
      <PartnerShareConsentDialog
        open={futureShareTarget !== null}
        defaultChecked
        isConnecting={connection.isSettingSharing}
        onConfirm={autoShare => {
          if (!futureShareTarget) return;

          connection.setContactSharing(
            futureShareTarget.partnerKey,
            futureShareTarget.advisorId,
            autoShare,
            () => setFutureShareTarget(null),
          );
        }}
        onCancel={() => setFutureShareTarget(null)}
      />
      <PartnerLegacyAttestationDialog
        open={legacyDialogOpen}
        isSharing={connection.isSharingLegacy || connection.isSharingContactsLegacy}
        onConfirm={() => {
          // Targeted contacts → share earlier clients with just those; empty = global "everyone" flow.
          if (legacyContactTargets.length > 0) {
            connection.shareContactsLegacy(
              legacyContactTargets.map(contact => ({
                partnerKey: contact.partnerKey,
                contactAdvisorId: contact.advisorId,
              })),
              () => {
                setLegacyDialogOpen(false);
                setLegacyContactTargets([]);
              },
            );
          } else {
            connection.confirmLegacyShare(() => setLegacyDialogOpen(false));
          }
        }}
        onCancel={() => {
          setLegacyDialogOpen(false);
          setLegacyContactTargets([]);
        }}
      />
      <PartnerDisconnectDialog
        open={disconnectTarget !== null}
        contactName={disconnectTarget ? contactDisplayName(disconnectTarget) : ''}
        isDisconnecting={connection.isDisconnecting}
        onConfirm={() => {
          if (!disconnectTarget) return;

          // Removing the connection clears any clients already shared with this contact and tears
          // the link down; "Stop sharing" (per-contact) is the way to pause without disconnecting.
          connection.disconnect(
            disconnectTarget.partnerKey,
            disconnectTarget.advisorId,
            'all',
            () => setDisconnectTarget(null),
          );
        }}
        onCancel={() => setDisconnectTarget(null)}
      />
    </>
  );

  const form = (
    <>
      <PartnerConnectionForm
        partners={connection.partners}
        isLoadingPartners={connection.isLoadingPartners}
        contacts={connection.contacts}
        isLoadingContacts={connection.isLoadingContacts}
        selectedPartnerKeys={connection.selectedPartnerKeys}
        selectedContactIds={connection.selectedContactIds}
        onTogglePartner={connection.togglePartner}
        onToggleContact={connection.toggleContact}
        onToggleContacts={connection.toggleContacts}
        onConnect={() => setConsentDialogOpen(true)}
        isConnecting={connection.isConnecting}
        onRequestOther={connection.requestOther}
        isRequestingOther={connection.isRequestingOther}
        onCheckContactExists={connection.checkContactHasAccount}
        isCheckingContact={connection.isCheckingContact}
        onRequestDisconnect={setDisconnectTarget}
        onSetContactSharing={(contact, autoShare) => {
          // Re-enabling future-client sharing always re-surfaces the share consent first; stopping
          // doesn't need consent and applies immediately.
          if (autoShare) {
            setFutureShareTarget(contact);
          } else {
            connection.setContactSharing(contact.partnerKey, contact.advisorId, false);
          }
        }}
        isSettingSharing={connection.isSettingSharing}
        onShareContactLegacy={(contact, share) => {
          // Stopping needs no consent. Sharing always surfaces the consent dialog first, then shares
          // with just this contact on confirm.
          if (!share) {
            connection.shareContactLegacy(contact.partnerKey, contact.advisorId, false);
          } else {
            setLegacyContactTargets([contact]);
            setLegacyDialogOpen(true);
          }
        }}
        isSharingContactLegacy={connection.isSharingContactLegacy}
        showLegacyShare={!hideLegacy && connection.hasLegacyClients}
        edjMode={isEdjPilot}
        hideInvite={variant === 'questionnaire'}
        prospective={variant === 'questionnaire'}
        idPrefix={`${variant}-`}
      />
      {dialogs}
    </>
  );

  if (variant === 'questionnaire') {
    // Embedded inside a questionnaire screen, which already supplies its own prompt/helper text,
    // so we render just the partner + contact selection (invite card hidden via `hideInvite`).
    return <div className="w-full text-left">{form}</div>;
  }

  if (variant === 'onboarding') {
    return (
      <div className="flex flex-col items-center justify-center px-4 sm:px-0">
        <div className="w-full max-w-2xl space-y-6 sm:space-y-8">
          <div className="text-center">
            <h2 className="text-balance text-xl font-semibold text-gray-950 sm:text-3xl">
              {isEdjPilot
                ? 'Which distribution partners do you work with?'
                : 'Connect your distribution partners'}
            </h2>
            <p className="text-foreground mt-2 text-balance text-sm sm:text-base">
              {isEdjPilot
                ? "Select the distribution partners and the people you work with at each firm. This is an indication of who you collaborate with — based on it, Waterlily shares client data with the selected partner to support your work, under Waterlily's client agreement."
                : 'Select the partners and the specific people you work with. Once connected, they can view the clients you share with them. Keep automatic sharing on to share every client you invite going forward, or turn it off to share clients one at a time from your Clients table.'}
            </p>
          </div>
          {form}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
      <AuthSeparator
        text="Distribution Partners"
        className="sm:col-span-6"
      />
      <p className="text-foreground text-sm sm:col-span-6">
        {isEdjPilot
          ? "Indicate the distribution partners and the people you work with at each firm. Based on your election, Waterlily shares client data with the selected partner to support your collaboration, under Waterlily's client agreement."
          : 'Connect the distribution partners you work with. Once connected, you can collaborate with the specific people you work with at each firm.'}
      </p>
      <div className="sm:col-span-6">{form}</div>

      {!hideLegacy &&
        connection.hasLegacyClients &&
        connection.hasConnections &&
        connection.hasUnsharedLegacyClients && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-4 sm:col-span-6">
            <div className="max-w-xl">
              <p className="text-foreground text-sm font-medium">
                Share your earlier clients with everyone
              </p>
              <p className="text-muted-foreground text-sm">
                Clients who joined before July 2, 2026 aren't shared automatically. Confirm you have
                their consent to share them with all your connected distribution partner contacts.
                You can also share them per contact from each contact's menu.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setLegacyContactTargets([]);
                setLegacyDialogOpen(true);
              }}
            >
              Share earlier clients
            </Button>
          </div>
        )}
    </div>
  );
}
